import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config/api';

export const Footer = ({ content, logoUrl }) => {
  const [footerSettings, setFooterSettings] = useState(null);
  const [brandSettings, setBrandSettings] = useState(null);
  const backendUrl = API_BASE;

  const defaultContent = {
    columns: [
      { title: 'Company', links: [{ label: 'About Us', href: '/pages/about' }, { label: 'Our Partners', href: '#' }, { label: 'Contact Us', href: '/pages/contact' }, { label: 'Partner With Us', href: '#' }] },
      { title: 'For Students', links: [{ label: 'Find Programs', href: '/programs' }, { label: 'Scholarships', href: '/scholarships' }, { label: 'Universities', href: '/universities' }] },
      { title: 'Quick Links', links: [{ label: 'Guides', href: '/blogs' }, { label: 'FAQs', href: '#' }, { label: 'Cashback Info', href: '#' }] },
      { title: 'Join Us', links: [{ label: 'As Counselor', href: '#' }, { label: 'As Referrer', href: '#' }, { label: 'As Recruiter', href: '#' }, { label: 'Career With Us', href: '#' }] },
    ],
    bottomLinks: [{ label: 'Privacy Policy', href: '/pages/privacy' }, { label: 'Terms of Service', href: '/pages/terms' }],
    social_links: { facebook: '', instagram: '', linkedin: '', youtube: '', tiktok: '', whatsapp: '' },
    copyright_text: 'Copyright @2026 All Rights Reserved by UNISAVE',
    company_text: 'Owned by Gen Alpha Visionaries Sdn Bhd (Reg. No: 202301023465 / 1517388-H).',
    powered_by_text: 'Website powered by Brandbytes Malaysia.'
  };

  // Fetch footer settings from CMS
  useEffect(() => {
    const fetchFooterSettings = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/public/footer-settings`);
        if (response.ok) {
          const data = await response.json();
          setFooterSettings(data.settings);
        }
      } catch (err) {
        console.log('Using default footer settings');
      }
    };
    fetchFooterSettings();
  }, [backendUrl]);

  // Fetch brand settings for logo
  useEffect(() => {
    const fetchBrandSettings = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/public/brand-settings`);
        if (response.ok) {
          const data = await response.json();
          setBrandSettings(data.settings);
        }
      } catch (err) {
        console.log('Using default logo');
      }
    };
    fetchBrandSettings();
  }, [backendUrl]);

  // Merge CMS settings with defaults
  const footerContent = content || (footerSettings ? {
    ...defaultContent,
    columns: footerSettings.columns?.length > 0 ? footerSettings.columns : defaultContent.columns,
    social_links: footerSettings.social_links || defaultContent.social_links,
    copyright_text: footerSettings.copyright_text || defaultContent.copyright_text,
    company_text: footerSettings.company_text || defaultContent.company_text,
    powered_by_text: footerSettings.powered_by_text || defaultContent.powered_by_text
  } : defaultContent);

  const socialLinks = footerContent.social_links || defaultContent.social_links;
  const copyrightText = footerContent.copyright_text || defaultContent.copyright_text;
  const companyText = footerContent.company_text || defaultContent.company_text;
  const poweredByText = footerContent.powered_by_text || defaultContent.powered_by_text;

  const defaultLogoUrl = '/placeholders/logo-default.svg';
  
  // Use logo from brand settings if available, otherwise use prop or default
  const footerLogo = brandSettings?.footer_logo || {};
  const currentLogoUrl = footerLogo.image_url || logoUrl || defaultLogoUrl;
  const logoAlt = footerLogo.alt_text || 'Unisave';
  const logoLink = footerLogo.link_url || '/';
  const logoWidth = footerLogo.width || 'auto';
  const logoHeight = footerLogo.height || 48;

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Social icon components
  const socialIcons = {
    facebook: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
    instagram: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>,
    linkedin: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>,
    youtube: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>,
    tiktok: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>,
    whatsapp: <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
  };

  const renderSocialIcons = (size = 'w-8 h-8') => {
    const platforms = ['facebook', 'instagram', 'linkedin', 'youtube', 'tiktok', 'whatsapp'];
    const platformLabels = {
      facebook: 'Visit our Facebook page',
      instagram: 'Visit our Instagram page',
      linkedin: 'Visit our LinkedIn page',
      youtube: 'Visit our YouTube channel',
      tiktok: 'Visit our TikTok page',
      whatsapp: 'Contact us on WhatsApp'
    };
    return platforms
      .filter(platform => socialLinks[platform])
      .map(platform => (
        <a key={platform} href={socialLinks[platform]} target="_blank" rel="noopener noreferrer" className={`${size} rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:bg-[#F2C900] hover:text-[#00224F] transition-all`} aria-label={platformLabels[platform]} title={platformLabels[platform]}>
          {socialIcons[platform]}
        </a>
      ));
  };

  return (
    <footer style={{ background: 'linear-gradient(rgb(0, 34, 79) 0%, rgb(66, 2, 67) 100%)' }} data-testid="footer">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10 py-10">
        {/* Desktop Layout */}
        <div className="hidden md:grid grid-cols-12 gap-8 items-start">
          {/* Logo */}
          <div className="col-span-2">
            <a href={logoLink}>
              <img 
                src={currentLogoUrl} 
                alt={logoAlt} 
                style={{ 
                  width: `${logoWidth}px`, 
                  height: `${logoHeight}px`,
                  filter: footerLogo.image_url ? 'none' : 'brightness(0) invert(1)', 
                  background: 'transparent' 
                }} 
                className="object-contain" 
              />
            </a>
          </div>

          {/* Links */}
          <div className="col-span-6">
            <div className="grid grid-cols-4 gap-6">
              {footerContent.columns.map((column, index) => (
                <div key={index}>
                  <h3 className="font-semibold text-white text-sm mb-3">{column.title}</h3>
                  <ul className="space-y-2">
                    {column.links.map((link, linkIndex) => (
                      <li key={linkIndex}><a href={link.href} className="text-xs text-white/50 hover:text-[#F2C900] transition-colors">{link.label}</a></li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Newsletter */}
          <div className="col-span-4">
            <h3 className="text-white font-semibold text-sm mb-3">Subscribe To Our Newsletter</h3>
            <div className="flex gap-2">
              <input type="email" placeholder="Your email" className="flex-1 h-10 px-3 rounded-lg bg-white/10 border border-white/20 text-white text-sm placeholder-white/40 focus:outline-none focus:border-[#F2C900]" />
              <button className="h-10 px-4 rounded-lg text-sm font-semibold transition-colors" style={{ backgroundColor: 'rgb(242, 201, 0)', color: '#00224F' }}>Go</button>
            </div>
            {/* Social icons */}
            <div className="flex items-center gap-3 mt-4">
              {renderSocialIcons('w-8 h-8')}
            </div>
          </div>
        </div>

        {/* Desktop Bottom bar */}
        <div className="hidden md:block mt-8 pt-6 border-t border-white/10">
          <div className="flex items-center justify-center gap-6 mb-3">
            {footerContent.bottomLinks.map((link, index) => (
              <a key={index} href={link.href} className="text-xs text-white/40 hover:text-[#F2C900] transition-colors">{link.label}</a>
            ))}
          </div>
          <div className="text-xs text-white/40 text-center">
            <p>{copyrightText}</p>
            <p>{companyText}</p>
            <p>{poweredByText}</p>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden py-4">
          {/* Logo centered */}
          <div className="flex justify-center mb-6">
            <a href={logoLink}>
              <img 
                src={currentLogoUrl} 
                alt={logoAlt} 
                style={{ 
                  width: `${logoWidth}px`, 
                  height: `${logoHeight}px`,
                  filter: footerLogo.image_url ? 'none' : 'brightness(0) invert(1)', 
                  background: 'transparent' 
                }} 
                className="object-contain" 
              />
            </a>
          </div>

          {/* Links - 2 columns, centered, minimized */}
          <div className="grid grid-cols-2 gap-3 text-center mb-4">
            {footerContent.columns.map((column, index) => (
              <div key={index}>
                <h3 className="font-semibold text-white text-xs mb-1.5">{column.title}</h3>
                <ul className="space-y-0.5">
                  {column.links.slice(0, 2).map((link, linkIndex) => (
                    <li key={linkIndex}><a href={link.href} className="text-[10px] text-white/50 hover:text-[#F2C900] transition-colors">{link.label}</a></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Newsletter */}
          <div className="text-center mb-4">
            <h3 className="text-white font-semibold text-xs mb-1.5">Subscribe To Our Newsletter</h3>
            <div className="flex gap-2 max-w-xs mx-auto">
              <input type="email" placeholder="Your email" className="flex-1 h-8 px-2.5 rounded-lg bg-white/10 border border-white/20 text-white text-xs placeholder-white/40 focus:outline-none focus:border-[#F2C900]" />
              <button className="h-8 px-3 rounded-lg text-xs font-semibold" style={{ backgroundColor: 'rgb(242, 201, 0)', color: '#00224F' }}>Go</button>
            </div>
          </div>

          {/* Social icons centered */}
          <div className="flex items-center justify-center gap-2 mb-4">
            {renderSocialIcons('w-7 h-7')}
          </div>

          {/* Scroll to top button */}
          <div className="flex justify-center mb-4">
            <button onClick={scrollToTop} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/60 hover:bg-[#F2C900] hover:text-[#00224F] transition-all" aria-label="Scroll to top" title="Scroll to top">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" /></svg>
            </button>
          </div>

          {/* Mobile Bottom bar */}
          <div className="pt-4 border-t border-white/10 text-center">
            <div className="flex items-center justify-center gap-4 mb-3">
              {footerContent.bottomLinks.map((link, index) => (
                <a key={index} href={link.href} className="text-xs text-white/40 hover:text-[#F2C900] transition-colors">{link.label}</a>
              ))}
            </div>
            <div className="text-xs text-white/40">
              <p>{copyrightText}</p>
              <p className="text-[10px] mt-1">{companyText}</p>
              <p className="text-[10px]">{poweredByText}</p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
